import React, { useEffect, useState } from "react";
import { PlatformBadge, PLATFORM_IDS } from "./components/PlatformBadge";
import { SiteHeader } from "./components/SiteHeader";
import { SiteFooter } from "./components/SiteFooter";
import { QcAuditPopup } from "./components/QcAuditPopup";
import { PickQuickFacts, PICKQUICK_FACTS_SCHEMA } from "./components/PickQuickFacts";
import { useDocumentMeta } from "./hooks/useDocumentMeta";
import { submitLeadWithWhatsApp } from "./lib/whatsappHandoff";
import {
  ArrowRight,
  BarChart3,
  CheckCircle2,
  Clock,
  LineChart,
  MessageCircle,
  PhoneCall,
  X,
  Zap,
} from "lucide-react";

import { Button } from "./components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import { toast } from "./components/ui/sonner";

import {
  platforms,
  painPoints,
  capabilities,
  processSteps,
  reasons,
  techFeatures,
  brandProfiles,
  faqItems,
  contactInfo,
} from "./mock/landingMock";
import { mediaSections, brandLogos } from "./mock/mediaMock";

const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

// FAQPage schema mirrors the visible <Accordion> content on the homepage so
// Google can surface these Q&A pairs directly in search results.
// (faqItems is already imported from ./mock/landingMock above.)
const buildFaqPageSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": items.map((f) => ({
    "@type": "Question",
    "name": f.question,
    "acceptedAnswer": { "@type": "Answer", "text": f.answer },
  })),
});

const LandingPage = () => {
  useDocumentMeta({
    title: "PickQuick | Quick Commerce Growth for FMCG Brands",
    description:
      "PickQuick is the quick commerce operating layer for India's FMCG brands. Onboarding, inventory, pricing and growth across Blinkit, Zepto, Instamart, JioMart.",
    ogType: "website",
    ogUrl: "https://letspickquick.com/",
    canonical: "https://letspickquick.com/",
    keywords: "quick commerce, quick commerce growth, quick commerce for brands, FMCG brands India, Blinkit Zepto Instamart, quick commerce operations, PickQuick",
    schema: [...PICKQUICK_FACTS_SCHEMA, buildFaqPageSchema(faqItems)],
  });
  const [formData, setFormData] = useState({
    name: "",
    brand: "",
    email: "",
    phone: "",
    city: "",
    productCategory: "",
    monthlyScale: "",
    context: "",
  });

  useEffect(() => {
    const saved = window.localStorage.getItem("pickquick_lead_draft");
    if (!saved) return;
    try {
      const parsed = JSON.parse(saved);
      setFormData((prev) => ({ ...prev, ...parsed }));
    } catch (err) {
      // Corrupted draft in localStorage, surface for debugging, then let the form start fresh
      console.warn("Failed to restore saved lead draft:", err);
    }
    // Mount-only effect: intentionally reads localStorage once and hydrates form state.
    // setFormData is stable (React setter); saved/parsed are function-local. No deps needed.
  }, []);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      window.localStorage.setItem("pickquick_lead_draft", JSON.stringify(next));
      return next;
    });
  };

  const handleMonthlyScaleChange = (value) => {
    setFormData((prev) => {
      const next = { ...prev, monthlyScale: value };
      window.localStorage.setItem("pickquick_lead_draft", JSON.stringify(next));
      return next;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Ensure lead_type is set so this end-to-end lead never lands in the
    // consulting or QC-Teams stream. `source` attributes the submission to
    // the homepage form specifically for per-page inbox triage.
    const payload = {
      name: formData.name,
      brand: formData.brand,
      email: formData.email,
      phone: formData.phone,
      city: formData.city,
      product_category: formData.productCategory,
      monthly_scale: formData.monthlyScale,
      context: formData.context,
      lead_type: "end-to-end",
      source: "homepage-book",
    };

    // WhatsApp is the primary action. The backend POST fires silently
    // afterwards and never blocks or reverses the handoff.
    submitLeadWithWhatsApp({
      payload,
      whatsapp: {
        title: "New PickQuick End-to-End Ops enquiry",
        source: "homepage-book",
        pagePath: "/",
        fields: {
          "Name": formData.name,
          "Brand": formData.brand,
          "Work email": formData.email,
          "Phone": formData.phone || "(not provided)",
          "City": formData.city || "(not provided)",
          "Product category": formData.productCategory || "(not provided)",
          "Approx. monthly sales": formData.monthlyScale || "(not selected)",
          "Context": formData.context || "(none)",
        },
      },
    }).then((res) => {
      if (res && res.ok) {
        res
          .json()
          .then((data) =>
            window.localStorage.setItem("pickquick_last_lead", JSON.stringify(data)),
          )
          .catch(() => {});
      }
    });

    toast.success("Opening WhatsApp with your details, the PickQuick team will pick up from there.");

    setFormData((prev) => ({
      ...prev,
      context: "",
    }));
  };

  const whatsappHref = "https://wa.me/918733085863";

  // NavLinks and site chrome are now provided by <SiteHeader /> and <SiteFooter />.

  return (
    <div className="min-h-screen bg-[var(--bg-deep)] text-white flex flex-col relative">
      <QcAuditPopup />

      {/* WhatsApp floating button */}
      <a
        href={whatsappHref}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-5 right-5 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-[#25D366] shadow-lg hover:shadow-xl transition-shadow duration-200"
      >
        <MessageCircle className="h-6 w-6 text-white" />
      </a>

      <SiteHeader />

      {/* Main */}
      <main className="flex-1">
        {/* Hero */}
        <section className="hero-section relative pt-24 pb-20 md:pt-32 md:pb-28 overflow-hidden" style={{ background: 'var(--bg-deep)' }}>
          {/* Mobile-only background image */}
          <div
            className="hero-bg-mobile"
            aria-hidden="true"
            style={{ backgroundImage: "url('/assets/hero-mobile-aisle.webp')" }}
          />
          {/* Mobile-only gradient overlay */}
          <div className="hero-overlay-mobile" aria-hidden="true" />
          {/* Desktop-only background image */}
          <div
            className="hero-bg-desktop"
            aria-hidden="true"
            style={{ backgroundImage: "url('/assets/hero-desktop-grid.webp')" }}
          />
          {/* Desktop-only gradient overlay */}
          <div className="hero-overlay-desktop" aria-hidden="true" />
          {/* Radial orange glow */}
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] rounded-full pointer-events-none" style={{ background: 'radial-gradient(ellipse at center, rgba(241,89,43,0.08) 0%, transparent 70%)' }} />

          <div className="container relative z-10">
            <div className="max-w-[1100px] mx-auto flex flex-col items-center md:items-start text-center md:text-left">
              {/* Headline */}
              <h1
                className="font-bold text-white max-w-[1100px]"
                style={{ fontSize: 'clamp(36px, 5.5vw, 72px)', lineHeight: 1.05, letterSpacing: '-0.02em' }}
                data-testid="hero-headline"
              >
                The Quick Commerce operating layer for India's top FMCG brands.
              </h1>

              {/* Subhead */}
              <p
                className="mt-7 text-[rgb(180,180,185)] max-w-[720px]"
                style={{ fontSize: 'clamp(16px, 1.5vw, 20px)', lineHeight: 1.55 }}
                data-testid="hero-subhead"
              >
                PickQuick runs your entire Quick Commerce engine, onboarding, inventory, dark-store logistics, and growth, as your internal QC division across 13 platforms including Blinkit, Zepto, Instamart, and JioMart.
              </p>

              {/* CTA Button */}
              <a
                href="https://calendar.app.google/q771ocWtwv6E75dr5"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-10 inline-flex items-center justify-center rounded-lg text-white font-semibold text-base px-8 transition-opacity hover:opacity-90"
                style={{ background: '#F1592B', height: '52px' }}
                data-testid="hero-cta"
              >
                Book a 30-minute revenue call
              </a>

              {/* Stat chips */}
              <div className="mt-12 flex flex-wrap items-center justify-center md:justify-start gap-x-0 gap-y-3" data-testid="hero-stats">
                <span className="flex items-center gap-2 px-4">
                  <span className="font-semibold text-[#F1592B]" style={{ fontSize: '18px' }}>₹146.7 Cr</span>
                  <span className="text-[rgb(180,180,185)]" style={{ fontSize: '15px' }}>monthly GMV</span>
                </span>
                <span className="hidden sm:block w-px h-5 bg-[rgba(255,255,255,0.15)]" />
                <span className="flex items-center gap-2 px-4">
                  <span className="font-semibold text-[#F1592B]" style={{ fontSize: '18px' }}>61 brands</span>
                  <span className="text-[rgb(180,180,185)]" style={{ fontSize: '15px' }}>live on QC</span>
                </span>
                <span className="hidden sm:block w-px h-5 bg-[rgba(255,255,255,0.15)]" />
                <span className="flex items-center gap-2 px-4">
                  <span className="font-semibold text-[#F1592B]" style={{ fontSize: '18px' }}>5.9M</span>
                  <span className="text-[rgb(180,180,185)]" style={{ fontSize: '15px' }}>orders per month</span>
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Platform logo strip */}
        <section style={{ background: 'var(--bg-deep)', padding: '40px 0 0 0' }}>
          <div className="container">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-14">
              <span style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '2px', textTransform: 'uppercase', color: '#8A8A8A' }} data-testid="live-across-label">
                LIVE ACROSS
              </span>
              <div className="flex items-center flex-wrap justify-center" style={{ gap: "20px" }} data-testid="platform-badge-row">
                {PLATFORM_IDS.map((id) => (
                  <PlatformBadge key={id} id={id} size="hero" />
                ))}
                <span
                  data-testid="platform-badge-total-chip"
                  aria-label="13 platforms total, 4 shown"
                  style={{
                    height: "56px",
                    borderRadius: "10px",
                    padding: "8px 18px",
                    display: "inline-flex",
                    alignItems: "center",
                    backgroundColor: "#1A1A1A",
                    border: "1px solid #262626",
                    color: "#B8B8B8",
                    fontSize: "14px",
                    fontWeight: 600,
                    letterSpacing: "0.02em",
                    whiteSpace: "nowrap",
                  }}
                >
                  + 9 more &middot; 13 total
                </span>
              </div>
            </div>
          </div>
        </section>


        {/* Section 6: Two-path qualifier block, directly below hero */}
        <PickQuickFacts />

        {/* Section 6: Two-path qualifier block, directly below hero */}
        <section
          id="qualifier"
          data-testid="qualifier-block"
          className="py-16 md:py-24"
          style={{ backgroundColor: "#0F0F0F" }}
        >
          <div className="container">
            <p
              className="text-xs font-semibold tracking-[0.16em] uppercase mb-4"
              style={{ color: "#8A8A8A", textAlign: "center" }}
            >
              Two ways to work with PickQuick
            </p>
            <h2
              className="text-white font-semibold mx-auto"
              style={{
                fontSize: "clamp(28px,3vw,40px)",
                lineHeight: 1.15,
                letterSpacing: "-0.02em",
                textAlign: "center",
                maxWidth: "760px",
              }}
            >
              Pick the path that <span style={{ color: "#F1592B" }}>fits</span> your team.
            </h2>

            <div className="mt-12 grid gap-6 md:grid-cols-2">
              {/* Path 1: End-to-end ops */}
              <button
                type="button"
                data-testid="qualifier-end-to-end"
                onClick={() => {
                  const el = document.getElementById("what-we-do");
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="rounded-2xl text-left p-8 md:p-10 h-full flex flex-col group"
                style={{
                  backgroundColor: "#1A1A1A",
                  border: "1px solid #262626",
                  cursor: "pointer",
                  transition: "border-color 0.2s ease, transform 0.2s ease",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#F1592B"; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#262626"; }}
              >
                <p
                  style={{
                    fontSize: "11px",
                    fontWeight: 700,
                    letterSpacing: "0.14em",
                    color: "#F1592B",
                    marginBottom: "16px",
                    textTransform: "uppercase",
                  }}
                >
                  End-to-End Ops
                </p>
                <h3
                  className="text-white"
                  style={{ fontSize: "24px", fontWeight: 600, letterSpacing: "-0.01em", lineHeight: 1.25 }}
                >
                  Want us to run your entire QC operation?
                </h3>
                <p className="mt-3" style={{ color: "#B8B8B8", fontSize: "15px", lineHeight: 1.55, flexGrow: 1 }}>
                  Onboarding, inventory, dark-store logistics, growth, all managed by PickQuick as your internal Quick Commerce division. Single control layer across 13 platforms.
                </p>
                <span
                  className="mt-6 inline-flex items-center gap-2"
                  style={{ color: "#F1592B", fontSize: "14px", fontWeight: 600 }}
                >
                  See what we run
                  <ArrowRight className="w-4 h-4" />
                </span>
              </button>

              {/* Path 2: Consulting */}
              <a
                href="/consulting"
                data-testid="qualifier-consulting"
                className="rounded-2xl p-8 md:p-10 h-full flex flex-col group"
                style={{
                  backgroundColor: "#1A0B05",
                  border: "1px solid #262626",
                  cursor: "pointer",
                  textDecoration: "none",
                  transition: "border-color 0.2s ease, transform 0.2s ease",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#F1592B"; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#262626"; }}
              >
                <p
                  style={{
                    fontSize: "11px",
                    fontWeight: 700,
                    letterSpacing: "0.14em",
                    color: "#F1592B",
                    marginBottom: "16px",
                    textTransform: "uppercase",
                  }}
                >
                  QC Consulting
                </p>
                <h3
                  className="text-white"
                  style={{ fontSize: "24px", fontWeight: 600, letterSpacing: "-0.01em", lineHeight: 1.25 }}
                >
                  Need help with a specific piece, logistics, marketing, growth, discoverability?
                </h3>
                <p className="mt-3" style={{ color: "#B8B8B8", fontSize: "15px", lineHeight: 1.55, flexGrow: 1 }}>
                  Keep your ops in-house. We plug in where you are leaking revenue, run the loop, and hand back the playbook. Free QC audit included.
                </p>
                <span
                  className="mt-6 inline-flex items-center gap-2"
                  style={{ color: "#F1592B", fontSize: "14px", fontWeight: 600 }}
                >
                  Explore QC Consulting
                  <ArrowRight className="w-4 h-4" />
                </span>
              </a>
            </div>
          </div>
        </section>


        {/* Quick Commerce gap */}
        <section id="gap" className="py-24" style={{background:'var(--bg-mid)'}}>
          <div className="container grid gap-10 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] items-start">
            <div className="space-y-4">
              <p className="h2">Quick Commerce isn't a channel anymore. It's where your category is <span style={{color:'var(--accent)'}}>decided.</span></p>
              <p className="body-md">
                Consumers expect 10, 20 minute delivery. Dark stores decide ranking. Platforms prioritize velocity. Most brands, even ones with ₹500+ Cr offline revenue, are losing share on Quick Commerce. Not because demand isn't there. Because execution is broken.
              </p>
            </div>
            <Card className="feature-card bg-[var(--bg-elevated)] border-[var(--border)]">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base text-white">
                  <BarChart3 className="h-4 w-4 text-white" />
                  Common barriers we remove
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-1">
                <ul className="space-y-2 text-sm text-[var(--text-body)]">
                  {painPoints.map((point) => (
                    <li key={point} className="flex items-start gap-2">
                      <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 text-[var(--accent)]" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>


        {/* Urja Case Study */}
        <section id="dairy-case-study" className="relative py-24 overflow-hidden" style={{background:'#1A0B05'}}>
          <div className="absolute top-1/3 left-1/3 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] rounded-full pointer-events-none" style={{background:'radial-gradient(ellipse at center, rgba(241,89,43,0.06) 0%, transparent 70%)'}} />
          <div className="container relative z-10">
            <div className="grid gap-12 lg:grid-cols-[1.22fr_1fr] items-start">
              <div className="space-y-6">
                <p style={{fontSize:'11px',fontWeight:600,letterSpacing:'2px',textTransform:'uppercase',color:'#8A8A8A'}}>
                  CASE STUDY &middot; MAHARASHTRA DAIRY BRAND
                </p>
                <h2 className="text-white font-bold leading-tight" style={{fontSize:'clamp(32px,3.6vw,48px)',lineHeight:1.1}}>
                  One dairy brand. Nine months.{' '}
                  <span style={{color:'var(--accent)'}}>4.7× the commitment.</span>
                </h2>
                <p style={{fontSize:'clamp(16px,1.4vw,18px)',lineHeight:1.65,color:'#B8B8B8'}}>
                  A leading Maharashtra-based dairy brand partnered with PickQuick in April 2025. We committed to a ₹7.5 Cr minimum revenue baseline. By January 2026, we had delivered ₹35.4 Cr, ₹27.8 Cr above projection. Consistent month-on-month scale. Protected rankings. Zero stock-outs in flagship pincodes.
                </p>
                <div className="flex flex-wrap items-center gap-x-0 gap-y-4 pt-2" data-testid="urja-stats">
                  <span className="flex flex-col items-start px-5">
                    <span className="font-semibold" style={{fontSize:'32px',color:'#F1592B'}}>₹7.5 Cr</span>
                    <span style={{fontSize:'12px',fontWeight:600,letterSpacing:'1.5px',textTransform:'uppercase',color:'#8A8A8A'}}>Committed</span>
                  </span>
                  <span className="hidden sm:block w-px h-10" style={{background:'#262626'}} />
                  <span className="flex flex-col items-start px-5">
                    <span className="font-semibold" style={{fontSize:'32px',color:'#F1592B'}}>₹35.4 Cr</span>
                    <span style={{fontSize:'12px',fontWeight:600,letterSpacing:'1.5px',textTransform:'uppercase',color:'#8A8A8A'}}>Delivered</span>
                  </span>
                  <span className="hidden sm:block w-px h-10" style={{background:'#262626'}} />
                  <span className="flex flex-col items-start px-5">
                    <span className="font-semibold" style={{fontSize:'32px',color:'#F1592B'}}>4.7×</span>
                    <span style={{fontSize:'12px',fontWeight:600,letterSpacing:'1.5px',textTransform:'uppercase',color:'#8A8A8A'}}>Performance vs commitment</span>
                  </span>
                </div>
              </div>
              <div
                className="flex items-center justify-center"
                style={{ width: '100%' }}
                data-testid="urja-chart-wrapper"
              >
                <img
                  id="urja-chart"
                  data-testid="urja-chart"
                  src="/assets/urja-chart.webp"
                  alt="URJA and PickQuick, monthly revenue: promised versus delivered, 4.7x performance vs commitment"
                  loading="lazy"
                  decoding="async"
                  width="1280"
                  height="720"
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    height: 'auto',
                    aspectRatio: '16 / 9',
                    objectFit: 'contain',
                    borderRadius: '12px',
                    display: 'block',
                  }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* What PickQuick does */}
        <section id="what-we-do" style={{ backgroundColor: '#0F0F0F' }}>
          {/* Full-width banner behind heading + subheading */}
          <div
            data-testid="what-we-do-banner"
            className="what-we-do-banner"
            style={{
              position: 'relative',
              width: '100%',
              overflow: 'hidden',
              backgroundColor: '#0F0F0F',
            }}
          >
            {/* Background image */}
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                inset: 0,
                backgroundImage: "url('/assets/operations-corridor.webp')",
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                filter: 'brightness(0.8) saturate(0.95)',
                zIndex: 0,
              }}
            />
            {/* Gradient overlay */}
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                inset: 0,
                background:
                  'linear-gradient(180deg, rgba(15,15,15,0.6) 0%, rgba(15,15,15,0.88) 70%, rgba(15,15,15,1) 100%)',
                zIndex: 1,
              }}
            />
            {/* Heading + subheading */}
            <div
              className="container"
              style={{
                position: 'relative',
                zIndex: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                paddingTop: '48px',
                paddingBottom: '32px',
              }}
            >
              <div className="max-w-2xl space-y-3">
                <h2
                  style={{
                    fontSize: '48px',
                    lineHeight: 1.1,
                    color: '#FFFFFF',
                    fontWeight: 600,
                    letterSpacing: '-0.02em',
                  }}
                >
                  What PickQuick <span style={{ color: '#F1592B' }}>does</span>
                </h2>
                <p style={{ fontSize: '18px', color: '#B8B8B8', lineHeight: 1.6 }}>
                  Three things, done end-to-end. As your internal QC division.
                </p>
              </div>
            </div>
          </div>

          {/* Three tiles on solid #0F0F0F */}
          <div className="container" style={{ paddingTop: '48px', paddingBottom: '64px' }}>
            <div
              className="grid md:grid-cols-3"
              style={{ gap: '24px' }}
            >
              {[
                {
                  id: 'get-live',
                  pill: 'Go-live in 14, 21 days',
                  title: 'Get Live',
                  body:
                    'Brand documentation, listings, catalog compliance, outright purchase orders, and dark-store inventory allocation, handled end-to-end, one flow, one POC.',
                },
                {
                  id: 'stay-live',
                  pill: 'Zero outages',
                  title: 'Stay Live',
                  body:
                    'Predictive replenishment driven by pincode-level demand signals. Real-time stock visibility. Outage alerts. We keep your brand on shelf in every flagship dark store across all 13 platforms we operate on, including Blinkit, Zepto, Instamart and JioMart.',
                },
                {
                  id: 'grow',
                  pill: 'Compounding velocity',
                  title: 'Grow',
                  body:
                    "Keyword strategy, promo design, platform category-team relationships, and daily velocity tuning. We don't just maintain your QC presence, we grow share, category by category.",
                },
              ].map((tile) => (
                <div
                  key={tile.id}
                  data-testid={`what-we-do-tile-${tile.id}`}
                  style={{
                    backgroundColor: '#1A1A1A',
                    border: '1px solid #262626',
                    padding: '32px',
                    borderRadius: '12px',
                  }}
                >
                  <span
                    style={{
                      display: 'inline-block',
                      backgroundColor: '#262626',
                      color: '#B8B8B8',
                      fontSize: '11px',
                      padding: '4px 10px',
                      borderRadius: '999px',
                      marginBottom: '20px',
                      letterSpacing: '0.02em',
                    }}
                  >
                    {tile.pill}
                  </span>
                  <h3
                    style={{
                      fontSize: '32px',
                      color: '#FFFFFF',
                      fontWeight: 600,
                      lineHeight: 1.15,
                      marginBottom: '12px',
                      letterSpacing: '-0.01em',
                    }}
                  >
                    {tile.title}
                  </h3>
                  <p style={{ fontSize: '16px', color: '#B8B8B8', lineHeight: 1.6 }}>
                    {tile.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Operating layer */}
        <section id="operating-layer" className="py-16" style={{ backgroundColor: '#000000' }}>
          <div className="container space-y-8">
            <h2
              style={{
                fontSize: '44px',
                lineHeight: 1.15,
                color: '#FFFFFF',
                fontWeight: 600,
                letterSpacing: '-0.02em',
                maxWidth: '960px',
                textAlign: 'left',
              }}
            >
              Without PickQuick, your brand runs three vendors, five spreadsheets, and zero visibility. With PickQuick, <span style={{ color: '#F1592B' }}>one stack</span>.
            </h2>
            <p
              style={{
                fontSize: '18px',
                color: '#B8B8B8',
                lineHeight: 1.6,
                maxWidth: '780px',
              }}
            >
              Most brands treat Blinkit, Zepto, Instamart, JioMart, and the nine other QC platforms as separate operational problems. Distributor there, inventory here, ads somewhere else. The gaps compound. PickQuick operates as a single control layer on top of all 13 platforms we are live on.
            </p>
            <div
              data-testid="architecture-diagram-wrapper"
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                paddingTop: '64px',
                paddingBottom: '64px',
              }}
            >
              <img
                id="architecture-diagram"
                data-testid="architecture-diagram"
                src="/assets/architecture-diagram.webp"
                alt="Quick commerce brand operations architecture, without PickQuick versus with PickQuick as the single control layer"
                loading="lazy"
                decoding="async"
                width="1600"
                height="900"
                className="architecture-diagram-img"
                style={{
                  width: '100%',
                  maxWidth: '1200px',
                  height: 'auto',
                  objectFit: 'contain',
                  border: '1px solid #262626',
                  borderRadius: '12px',
                  display: 'block',
                }}
              />
            </div>
          </div>
        </section>

        {/* Commercials */}
        <section id="commercials" style={{ backgroundColor: '#1A0B05' }}>
          {/* Full-width banner with heading + intro */}
          <div
            data-testid="commercials-banner"
            className="commercials-banner"
            style={{
              position: 'relative',
              width: '100%',
              overflow: 'hidden',
              backgroundColor: '#1A0B05',
            }}
          >
            {/* Background image */}
            <img
              src="/assets/commercials-boxes.webp"
              alt=""
              aria-hidden="true"
              loading="lazy"
              decoding="async"
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                filter: 'brightness(0.85) saturate(0.9)',
                zIndex: 0,
              }}
            />
            {/* Dark gradient overlay */}
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                inset: 0,
                background:
                  'linear-gradient(to bottom, rgba(26, 11, 5, 0.4) 0%, rgba(26, 11, 5, 0.95) 100%)',
                zIndex: 1,
              }}
            />
            {/* Content on top of banner */}
            <div
              className="container"
              style={{
                position: 'relative',
                zIndex: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                paddingTop: '40px',
                paddingBottom: '32px',
              }}
            >
              <h2
                className="commercials-banner-h2"
                style={{
                  lineHeight: 1.1,
                  color: '#FFFFFF',
                  fontWeight: 600,
                  letterSpacing: '-0.02em',
                  textAlign: 'center',
                }}
              >
                How our <span style={{ color: '#F1592B' }}>commercials</span> work.
              </h2>
              <p
                className="commercials-banner-intro"
                style={{
                  color: '#B8B8B8',
                  lineHeight: 1.6,
                  maxWidth: '760px',
                  margin: '16px auto 0',
                  textAlign: 'center',
                }}
              >
                Most QC enablement partners take a management fee and send inventory on Sale-or-Return. When your product doesn&apos;t sell, you eat the returns. We don&apos;t work that way.
              </p>
            </div>
          </div>

          {/* Rest of the section on solid #1A0B05 */}
          <div className="container space-y-10 py-16">
            {/* Sub-heading */}
            <p
              style={{
                fontSize: '28px',
                lineHeight: 1.3,
                color: '#FFFFFF',
                fontWeight: 500,
                letterSpacing: '-0.01em',
                maxWidth: '840px',
                margin: '0 auto',
                textAlign: 'center',
              }}
            >
              PickQuick issues <span style={{ color: '#F1592B' }}>outright purchase orders</span> and commits to a <span style={{ color: '#F1592B' }}>12-month minimum revenue baseline</span>, in writing.
            </p>

            {/* Comparison table */}
            <div
              data-testid="commercials-comparison-table"
              style={{
                maxWidth: '960px',
                margin: '0 auto',
                width: '100%',
              }}
            >
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                {/* Header cells */}
                <div
                  style={{
                    fontSize: '14px',
                    color: '#8A8A8A',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    padding: '24px',
                    borderBottom: '1px solid #262626',
                  }}
                >
                  Most QC enablement partners
                </div>
                <div
                  style={{
                    fontSize: '14px',
                    color: '#FFFFFF',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    padding: '24px',
                    backgroundColor: '#F1592B',
                  }}
                >
                  With PickQuick
                </div>

                {/* Rows */}
                {[
                  ['SOR inventory', 'Outright POs'],
                  ['Long credit cycles', 'Paid upfront'],
                  ['Return liability on the brand', 'Zero return liability'],
                  ['APOB / multi-POC compliance burden', 'Single POC, zero APOB'],
                  ['Best-effort growth commitment', '12-month minimum revenue commitment, written'],
                  ['Fragmented distribution + warehousing + ads', 'Bundled: distribution + warehousing + ads'],
                ].map(([left, right]) => (
                  <React.Fragment key={left}>
                    <div
                      style={{
                        fontSize: '16px',
                        color: '#8A8A8A',
                        padding: '0 24px',
                        height: '56px',
                        display: 'flex',
                        alignItems: 'center',
                        borderBottom: '1px solid #262626',
                      }}
                    >
                      {left}
                    </div>
                    <div
                      style={{
                        fontSize: '16px',
                        color: '#FFFFFF',
                        fontWeight: 500,
                        padding: '0 24px',
                        height: '56px',
                        display: 'flex',
                        alignItems: 'center',
                        borderBottom: '1px solid #262626',
                      }}
                    >
                      {right}
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Company statement (declaration, not a quotation) */}
            <div
              data-testid="commercials-company-statement"
              style={{
                maxWidth: '820px',
                margin: '0 auto',
                padding: '32px 40px',
                textAlign: 'center',
                borderLeft: '3px solid #F1592B',
                background: 'rgba(241, 89, 43, 0.04)',
                borderRadius: '8px',
              }}
            >
              <p
                style={{
                  fontSize: '11px',
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  color: '#F1592B',
                  fontWeight: 700,
                  marginBottom: '16px',
                }}
              >
                How we work
              </p>
              <p
                style={{
                  fontSize: '28px',
                  lineHeight: 1.35,
                  color: '#FFFFFF',
                  fontStyle: 'normal',
                  fontWeight: 600,
                  letterSpacing: '-0.01em',
                }}
              >
                We evaluate 40+ brands every month. We commit to 8 to 12. If we can&apos;t commit to revenue, we won&apos;t take the mandate.
              </p>
            </div>

            {/* CTA */}
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button
                data-testid="commercials-cta-book-call"
                onClick={() => scrollToSection('book')}
                style={{
                  backgroundColor: '#F1592B',
                  color: '#FFFFFF',
                  height: '52px',
                  padding: '0 24px',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  letterSpacing: '-0.005em',
                }}
              >
                Book a 30-minute revenue call
              </button>
            </div>
          </div>
        </section>



        {/* Trusted by brands, infinite carousel */}
        <section className="py-16" style={{ backgroundColor: '#0F0F0F' }}>
          <div className="container">
            <h2
              className="brands-carousel-heading"
              style={{
                fontSize: '40px',
                lineHeight: 1.15,
                color: '#FFFFFF',
                fontWeight: 600,
                letterSpacing: '-0.02em',
              }}
            >
              Trusted by <span style={{ color: '#F1592B' }}>61 brands</span> across 52 cities.
            </h2>
          </div>

          <div
            className="brands-carousel"
            data-testid="brands-carousel"
          >
            <div className="brands-carousel-track">
              {[...brandLogos, ...brandLogos].map((logo, idx) => (
                <div
                  key={`${logo.id}-${idx}`}
                  className={
                    logo.extraPadding
                      ? "brands-carousel-item brands-carousel-item--tight"
                      : "brands-carousel-item"
                  }
                  aria-hidden={idx >= brandLogos.length}
                >
                  {logo.url ? (
                    <img src={logo.url} alt={`${logo.name} live on PickQuick quick commerce`} loading="lazy" decoding="async" className="brands-carousel-img" />
                  ) : (
                    <span style={{ color: '#111', fontWeight: 600, fontSize: '15px' }}>{logo.name}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA, Book a call */}
        <section id="book" className="py-20" style={{ backgroundColor: '#000000' }}>
          <div className="container space-y-10">
            {/* Heading */}
            <h2
              style={{
                fontSize: '48px',
                lineHeight: 1.1,
                color: '#FFFFFF',
                fontWeight: 600,
                letterSpacing: '-0.02em',
                textAlign: 'center',
              }}
            >
              Book a 30-minute <span style={{ color: '#F1592B' }}>revenue call</span>.
            </h2>

            {/* Subheading */}
            <p
              style={{
                fontSize: '18px',
                color: '#B8B8B8',
                lineHeight: 1.6,
                maxWidth: '780px',
                margin: '0 auto',
                textAlign: 'center',
              }}
            >
              We&apos;ll come prepared with a view of your category across the 13 platforms we operate on, including Blinkit, Zepto, Instamart, and JioMart, lost-revenue zones, competitor positions, and a framework for a potential guarantee.
            </p>

            {/* Two-column layout */}
            <div
              className="grid"
              style={{
                gridTemplateColumns: 'minmax(0, 60fr) minmax(0, 40fr)',
                gap: '32px',
                alignItems: 'start',
              }}
            >
              {/* Google Calendar booking button (opens in new tab; Google's appointment page cannot be iframed) */}
              <div
                data-testid="book-calendar-cta"
                style={{
                  minHeight: '260px',
                  borderRadius: '12px',
                  backgroundColor: '#0F0F0F',
                  border: '1px solid #262626',
                  padding: '48px 32px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  gap: '16px',
                }}
              >
                <p
                  style={{
                    fontSize: '11px',
                    letterSpacing: '0.16em',
                    textTransform: 'uppercase',
                    color: '#8A8A8A',
                    fontWeight: 600,
                  }}
                >
                  Pick a time
                </p>
                <p style={{ fontSize: '22px', color: '#FFFFFF', lineHeight: 1.3, fontWeight: 500, maxWidth: '460px' }}>
                  Grab a 30-minute slot directly on our Google Calendar. We come prepared with your category view.
                </p>
                <a
                  href="https://calendar.app.google/q771ocWtwv6E75dr5"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="book-google-calendar-cta"
                  style={{
                    marginTop: '8px',
                    backgroundColor: '#F1592B',
                    color: '#FFFFFF',
                    height: '52px',
                    padding: '0 24px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: 500,
                    textDecoration: 'none',
                  }}
                >
                  Open Google Calendar
                </a>
                <p style={{ fontSize: '13px', color: '#8A8A8A' }}>
                  Opens in a new tab.
                </p>
              </div>

              {/* Share details form (40%) */}
              <div
                data-testid="book-share-details-card"
                style={{
                  backgroundColor: '#1A1A1A',
                  border: '1px solid #262626',
                  padding: '32px',
                  borderRadius: '12px',
                }}
              >
                <form className="space-y-4" onSubmit={handleSubmit}>
                  <div>
                    <label
                      htmlFor="book-name"
                      style={{ display: 'block', fontSize: '13px', color: '#B8B8B8', marginBottom: '6px' }}
                    >
                      Full Name
                    </label>
                    <input
                      id="book-name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange('name')}
                      style={{
                        width: '100%',
                        height: '44px',
                        padding: '0 16px',
                        backgroundColor: '#0F0F0F',
                        border: '1px solid #262626',
                        borderRadius: '8px',
                        color: '#FFFFFF',
                        fontSize: '15px',
                        outline: 'none',
                      }}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="book-brand"
                      style={{ display: 'block', fontSize: '13px', color: '#B8B8B8', marginBottom: '6px' }}
                    >
                      Brand Name
                    </label>
                    <input
                      id="book-brand"
                      type="text"
                      required
                      value={formData.brand}
                      onChange={handleChange('brand')}
                      style={{
                        width: '100%',
                        height: '44px',
                        padding: '0 16px',
                        backgroundColor: '#0F0F0F',
                        border: '1px solid #262626',
                        borderRadius: '8px',
                        color: '#FFFFFF',
                        fontSize: '15px',
                        outline: 'none',
                      }}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="book-email"
                      style={{ display: 'block', fontSize: '13px', color: '#B8B8B8', marginBottom: '6px' }}
                    >
                      Work Email
                    </label>
                    <input
                      id="book-email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange('email')}
                      style={{
                        width: '100%',
                        height: '44px',
                        padding: '0 16px',
                        backgroundColor: '#0F0F0F',
                        border: '1px solid #262626',
                        borderRadius: '8px',
                        color: '#FFFFFF',
                        fontSize: '15px',
                        outline: 'none',
                      }}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="book-phone"
                      style={{ display: 'block', fontSize: '13px', color: '#B8B8B8', marginBottom: '6px' }}
                    >
                      Phone (with country code)
                    </label>
                    <input
                      id="book-phone"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={handleChange('phone')}
                      style={{
                        width: '100%',
                        height: '44px',
                        padding: '0 16px',
                        backgroundColor: '#0F0F0F',
                        border: '1px solid #262626',
                        borderRadius: '8px',
                        color: '#FFFFFF',
                        fontSize: '15px',
                        outline: 'none',
                      }}
                    />
                  </div>
                  <button
                    type="submit"
                    data-testid="book-share-details-submit"
                    style={{
                      width: '100%',
                      height: '48px',
                      backgroundColor: '#F1592B',
                      color: '#FFFFFF',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '15px',
                      fontWeight: 500,
                      cursor: 'pointer',
                      marginTop: '4px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                    }}
                  >
                    <MessageCircle className="h-4 w-4" />
                    Send on WhatsApp
                  </button>
                  <p style={{ fontSize: '13px', color: '#8A8A8A', marginTop: '8px' }}>
                    Opens WhatsApp with your details pre-filled. We reply within one business day.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ + contact */}
        <section id="contact" className="py-16" style={{ backgroundColor: '#0F0F0F' }}>
          <div className="container grid gap-10 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] items-start">
            <div className="space-y-5">
              <div className="space-y-3">
                <p className="h2">Questions on how this would <span style={{color:'var(--accent)'}}>work</span> for your brand?</p>
                <p className="body-md">
                  Here are answers to what most brand leaders ask us before they decide to scale on Quick Commerce.
                </p>
              </div>
              <Accordion type="single" collapsible className="w-full">
                {faqItems.map((item) => (
                  <AccordionItem
                    key={item.id}
                    value={item.id}
                    className="border-b border-[#262626] bg-[#0F0F0F] data-[state=open]:bg-[#1A1A1A] px-4 rounded-md mb-2 transition-colors"
                  >
                    <AccordionTrigger className="text-left text-sm text-white hover:no-underline">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm" style={{ color: '#B8B8B8' }}>
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
            <Card style={{ backgroundColor: '#1A1A1A', border: '1px solid #262626' }} className="feature-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2 text-white">
                  <Zap className="h-4 w-4 text-white" />
                  Let us scale your brand on Quick Commerce
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-1 text-sm text-[var(--text-body)] space-y-3">
                <p>
                  Quick Commerce is now one of the most important distribution channels in India. PickQuick ensures
                  your brand enters it the right way.
                </p>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#0F0F0F] text-white text-xs font-semibold border border-[#262626]">
                      @
                    </span>
                    <a href={`mailto:${contactInfo.email}`} style={{ color: '#F1592B' }} className="hover:underline">
                      {contactInfo.email}
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[#0F0F0F] text-white text-xs font-semibold border border-[#262626]">
                      +
                    </span>
                    <a href={`tel:${contactInfo.phone.replace(/\s/g, "")}`} style={{ color: '#F1592B' }} className="hover:underline">
                      {contactInfo.phone}
                    </a>
                  </div>
                </div>
                <p className="text-[11px] text-[var(--text-muted)]">
                  Prefer WhatsApp or a quick call? Mention it in the form above and we will align to your preference.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>
      </main>

      {/* Footer */}
      <SiteFooter />
    </div>
  );
};

export default LandingPage;
